import type { bcs } from '@mysten/sui/bcs';

export class SendAssertionError extends Error {
  constructor(message: string) {
    super(`Transaction does not match what you approved: ${message}`);
    this.name = 'SendAssertionError';
  }
}

/**
 * Distinguished because it has an innocent explanation the others do not: the
 * backend selects coins over gRPC and this check reads GraphQL, so the two can
 * momentarily disagree about what the address owns.
 */
export class UnverifiedCoinOwnershipError extends SendAssertionError {}

export interface IAssertInput {
  txBytes: string;
  expected: {
    sender: string;
    recipient: string;
    /**
     * A coin's type is not encoded in the bytes, so callers enforce it by
     * scoping `ownedInputObjectIds` to that type. It only decides whether the
     * gas coin may serve as the split source.
     */
    coinType: string;
    amount: bigint;
    /**
     * Must come from the client's own chain connection, never from Gamma's
     * API — otherwise a compromised API vouches for its own claim.
     */
    ownedInputObjectIds: string[];
    ownedGasObjectIds: string[];
    chainIdentifier?: string;
  };
}

export type ParsedCommand = ReturnType<typeof bcs.Command.parse>;
export type ParsedCallArg = ReturnType<typeof bcs.CallArg.parse>;
export type ParsedArgument = ReturnType<typeof bcs.Argument.parse>;
export type ParsedSplitCommand = Extract<ParsedCommand, { $kind: 'SplitCoins' }>;
export type ParsedMergeCommand = Extract<ParsedCommand, { $kind: 'MergeCoins' }>;
export type SendExpectation = IAssertInput['expected'];

export type TransferValidationContext = {
  commands: ParsedCommand[];
  inputs: ParsedCallArg[];
  gasOwner: string;
  expiration: ReturnType<typeof bcs.TransactionData.parse>['V1']['expiration'];
  expected: SendExpectation;
};

export type TransferValidationStrategy = (context: TransferValidationContext) => void;
